import styled from 'styled-components';

export const DashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ── Player metric card (ARMSCORE / TOTAL ARM STRENGTH) ── */
export const PlayerMetricCard = styled.div`
  background: #fbfcfd;
  border-radius: 12px;
  border: 1px solid #d8dde3;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(15, 35, 52, 0.06);
`;

export const PlayerMetricCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

export const PlayerMetricTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  text-transform: uppercase;
  font-family: 'Proxima Nova Bold', sans-serif;
  color: #2b323b;
`;

export const PlayerMetricInfoTooltipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  &:hover > div,
  &:focus-within > div {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
`;

export const PlayerMetricInfoIcon = styled.button`
  border: 1px solid #d3d8e0;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #5d6e81;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
`;

export const PlayerMetricTooltipBubble = styled.div`
  position: absolute;
  z-index: 1200;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(100% + 10px);
  min-width: 200px;
  max-width: min(280px, calc(100vw - 24px));
  padding: 10px 12px;
  border-radius: 8px;
  background: #ffffff;
  color: #000000;
  font-size: 12px;
  line-height: 1.45;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  box-shadow: 0 8px 24px rgba(15, 35, 52, 0.06);
  border: 1px solid #d8dde3;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.12s ease, visibility 0.12s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: #ffffff transparent transparent transparent;
  }
`;

export const PlayerMetricSingleTile = styled.div<{ $variant?: 'blue' | 'green' }>`
  margin-top: 12px;
  border-radius: 16px;
  background: ${({ $variant }) =>
    $variant === 'green'
      ? 'linear-gradient(90deg, #fbfcfd 0%, #eaf9ec 100%)'
      : 'linear-gradient(90deg, #fbfcfd 0%, #e3effb 100%)'};
  border: ${({ $variant }) =>
    $variant === 'green' ? '1px solid #d7efda' : '1px solid #dbe9f7'};
  padding: 8px 8px 8px 16px;
  position: relative;
  min-height: 108px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const PlayerMetricTileLabel = styled.div`
  font-size: 14px;
  font-family: 'Proxima Nova Bold', sans-serif;
  font-weight: 700;
  color: #38424d;
  position: relative;
  z-index: 1;
`;

export const PlayerMetricValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  position: relative;
  z-index: 1;
`;

export const PlayerMetricNumber = styled.span`
  font-size: 36px;
  font-family: 'Proxima Nova Bold', sans-serif;
  font-weight: 700;
  color: #2b323b;
  line-height: 1;
`;

export const PlayerMetricUnit = styled.span`
  font-size: 16px;
  font-family: 'Proxima Nova', sans-serif;
  color: #38424d;
`;

export const PlayerMetricIconWrap = styled.div`
  position: absolute;
  right: -4px;
  top: 55%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 0;

  svg {
    display: block;
    width: 82px;
    height: 78px;
  }
`;

export const MainValue = styled.p`
  font-size: 64px;
  font-weight: 800;
  color: #2563eb;
  line-height: 1;
  margin: 4px 0 0 0;
`;

export const MainUnit = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #2563eb;
`;

export const PeerInfo = styled.div`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.6;
`;

export const GradientArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 16px;

  svg {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 55%;
  }
`;

export const InfoIcon = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 18px;
  line-height: 1;
  padding: 0;
`;

/* ── Blue stat card (TESTING / TRAINING) ── */
export const StatCard = styled.div`
  background: linear-gradient(180deg, #186AC7 0%, #3B86DC 100%);
  border-radius: 16px;
  border: 2px solid #3396FF;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(0, 0, 0, 0.02);
  padding: 24px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StatCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StatLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const StatBody = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StatMainValue = styled.p`
  font-size: 60px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1.5px;
  margin: 0;
`;

export const StatDetails = styled.div`
  text-align: right;
  font-size: 19px;
  font-weight: 500;
  line-height: 1.55;

  span {
    font-weight: 700;
    margin-left: 8px;
  }
`;

export const StreakBox = styled.div`
  background: #FBFCFD;
  border: 1px solid #D8DDE3;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(0, 0, 0, 0.02);
  border-radius: 16px;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

export const StreakLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  margin: 0 0 4px 0;
  color: #0056B2;
`;

export const StreakValue = styled.p`
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
  color: #0056B2;
`;

export const StreakRecommended = styled.div`
  text-align: right;
  font-size: 19px;
  font-weight: 500;
  color: #0056B3;
  line-height: 1.4;
`;

export const RecommendedLabel = styled.p`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 400;
  color: #0056B2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

/* ── Filter bar ── */
export const LevelSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const LevelText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const LevelBadge = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 7px 13px;
  gap: 4px;
  height: 30px;
  border: 1px solid #e6e7eb;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
`;

/* ── Weekly Activity ── */
export const WeeklySection = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  background: #FBFCFD;
  border: 1px solid #D8DDE3;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(0, 0, 0, 0.02);
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  padding: 24px;

  @media (max-width: 768px) {
    overflow: visible;
    padding: 16px;
  }
`;

export const WeeklyHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 24px 0px 24px 24px;
  align-self: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 16px;
  }
`;

export const WeeklyTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #1e293b;
  text-transform: uppercase;
`;

export const DateRangeBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 240px;
  height: 50px;
  padding: 16px 32px 16px 12px;
  background: #fff;
  color: #334155;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
`;

export const DateRangeChevron = styled.span`
  position: absolute;
  right: 8px;
  top: 0;
  bottom: 0;
  width: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748B;
`;

export const TableContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: 1px solid rgba(158, 163, 183, 0.26);
  isolation: isolate;
`;

export const WeeklyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

export const TableHead = styled.thead`
  background: #6EA5DF;
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const Th = styled.th<{ first?: boolean }>`
  padding: 17px 8px;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #fff;
  text-align: center;
  width: ${({ first }) => (first ? '160px' : 'auto')};
  ${({ first }) => first && 'text-align: left; padding-left: 20px;'}
`;

export const Td = styled.td<{ first?: boolean }>`
  padding: 12px 16px 18px 16px;
  font-size: 16px;
  line-height: 20px;
  font-weight: ${({ first }) => (first ? '600' : '400')};
  color: #000000;
  text-align: center;
  border-bottom: 1px solid rgba(99, 101, 112, 0.21);
  ${({ first }) => first && 'text-align: left; padding-left: 16px;'}

  tr:last-child & {
    border-bottom: none;
  }
`;

export const Tr = styled.tr<{ $dark?: boolean }>`
  background: ${({ $dark }) => ($dark ? '#DBEDFF' : '#F0F7FF')};
`;

export const ThrowingValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
`;

export const CompletedBadge = styled.span`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 3px 9px;
  background: #E2F7EB;
  border-radius: 9999px;
  color: #03A95E;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  white-space: nowrap;
`;

export const AlertBadge = styled.span<{ variant: 'watch' | 'warning' | 'medical' | 'noAlert' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;

  ${({ variant }) => variant === 'watch'   && `background: #DB8F0A; color: #FCF6EE;`}
  ${({ variant }) => variant === 'warning' && `background: #8C2BEE; color: #FDF9F4;`}
  ${({ variant }) => variant === 'medical' && `background: #16A34A; color: #F0FDF4;`}
  ${({ variant }) => variant === 'noAlert' && `background: #E5F2FF; color: #017CFF;`}
`;

/* ── Tooltip ── */
export const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const TooltipBox = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: normal;
  text-transform: none;
  border-radius: 8px;
  padding: 8px 12px;
  width: 260px;
  z-index: 10;
  pointer-events: none;
  transition: opacity 0.15s;

  ${TooltipWrapper}:hover & {
    visibility: visible;
    opacity: 1;
  }
`;

/* ── Week navigation ── */
export const WeekNav = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
`;

export const DateRangeWrap = styled.div`
  position: relative;
`;

export const WeekRangeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
`;

export const WeekNavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  height: 49px;
  border: 1px solid #E2E8F0;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #334155;
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;

  svg {
    color: #607184;
  }

  &:hover {
    background: #f1f5f9;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── Week list dropdown (infinite scroll) ── */
export const WeekListDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 240px;
  z-index: 5;
  background: #fff;
  border: 1px solid #E2E8F0;
  box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const WeekListScroll = styled.div`
  max-height: 318px;
  overflow-y: auto;
`;

export const WeekListItem = styled.div<{ $active?: boolean }>`
  box-sizing: border-box;
  padding: 16px 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  color: ${props => (props.$active ? '#2B323B' : '#334155')};
  background: ${props => (props.$active ? '#E5F2FF' : 'transparent')};
  cursor: pointer;

  &:hover {
    background: ${props => (props.$active ? '#E5F2FF' : '#F1F5F9')};
  }
`;

/* ── Card loading placeholder ── */
export const CardPlaceholder = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
`;

/* ── Date picker dropdown wrapper ── */
export const CustomDateDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  z-index: 100;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;
